import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getFxRates } from "./fx-rates.functions";

const COMMON_CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "SEK", "NOK", "NZD", "SGD", "HKD", "MXN", "BRL", "ZAR", "INR", "CNY", "KRW", "PLN", "DKK"] as const;
export type CurrencyCode = string;

const LOCALE_TO_CURRENCY: Record<string, string> = {
  US: "USD", CA: "CAD", GB: "GBP", AU: "AUD", NZ: "NZD", JP: "JPY", CH: "CHF",
  DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", IE: "EUR", PT: "EUR", BE: "EUR", AT: "EUR", FI: "EUR",
  SE: "SEK", NO: "NOK", DK: "DKK", PL: "PLN",
  SG: "SGD", HK: "HKD", MX: "MXN", BR: "BRL", ZA: "ZAR", IN: "INR", CN: "CNY", KR: "KRW",
};

// Same fallback shape used server-side, in case the server function itself is
// unreachable (offline, ad-blocker on same-origin). Kept current (~2026).
const CLIENT_FALLBACK_RATES: Record<string, number> = {
  USD: 1, EUR: 0.87, GBP: 0.74, CAD: 1.40, AUD: 1.43, JPY: 162, CHF: 0.81,
  SEK: 9.7, NOK: 10.2, DKK: 6.5, NZD: 1.62, SGD: 1.29, HKD: 7.80, MXN: 17.4,
  BRL: 5.08, ZAR: 18.0, INR: 96.4, CNY: 6.78, KRW: 1370, PLN: 3.70,
};

function detectCurrencyFromLocale(): string {
  if (typeof navigator === "undefined") return "USD";
  const lang = navigator.language || "en-US";
  const region = lang.split("-")[1]?.toUpperCase();
  return (region && LOCALE_TO_CURRENCY[region]) || "USD";
}

export type FxSource = "exchangerate-api" | "frankfurter" | "fallback";

interface Ctx {
  display: string;
  setDisplay: (c: string) => void;
  rates: Record<string, number>;
  ready: boolean;
  convert: (usd: number) => number;
  format: (usd: number) => string;
  available: readonly string[];
  source: FxSource;
  fetchedAt: string | null;
  upstreamUpdatedAt: string | null;
  stale: boolean;
}

const CurrencyCtx = createContext<Ctx | null>(null);

const STORAGE_KEY = "w2b-currency";
const TOOLTIP_BASE = "Display only — buyer and seller arrange payment directly";

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [display, setDisplayState] = useState<string>("USD");

  useEffect(() => {
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    setDisplayState(stored || detectCurrencyFromLocale());
  }, []);

  const getFxRatesFn = useServerFn(getFxRates);
  const { data, isSuccess } = useQuery({
    queryKey: ["fx-rates"],
    queryFn: () => getFxRatesFn(),
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rates = data?.rates ?? CLIENT_FALLBACK_RATES;
  const source: FxSource = data?.source ?? "fallback";
  const fetchedAt = data?.fetchedAt ?? null;
  const upstreamUpdatedAt = data?.upstreamUpdatedAt ?? null;
  const stale = data?.stale ?? !isSuccess;
  const ready = isSuccess || !!data;

  const setDisplay = (c: string) => {
    setDisplayState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch {}
  };

  const value = useMemo<Ctx>(() => ({
    display,
    setDisplay,
    rates,
    ready,
    convert: (usd: number) => usd * (rates[display] ?? 1),
    format: (usd: number) => {
      const v = usd * (rates[display] ?? 1);
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: display, maximumFractionDigits: v >= 100 ? 0 : 2 }).format(v);
      } catch {
        return `${display} ${v.toFixed(0)}`;
      }
    },
    available: COMMON_CURRENCIES,
    source,
    fetchedAt,
    upstreamUpdatedAt,
    stale,
  }), [display, rates, ready, source, fetchedAt, upstreamUpdatedAt, stale]);

  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyCtx);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return "unknown";
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return "just now";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function priceTooltip(source: FxSource, upstreamUpdatedAt: string | null, stale: boolean): string {
  if (stale || source === "fallback") {
    return `${TOOLTIP_BASE} · Using cached fallback rates (live rate service unreachable)`;
  }
  const label = source === "frankfurter" ? "Frankfurter (ECB)" : "ExchangeRate-API";
  return `${TOOLTIP_BASE} · Rates from ${label}, updated ${fmtRelative(upstreamUpdatedAt)}`;
}

/**
 * Always shows the USD price (canonical) with a smaller grey converted line below.
 * Conversion is display-only — buyer and seller arrange payment directly.
 */
export function Price({
  usd,
  size = "md",
  align = "left",
  hideConvertedIfUSD = true,
}: {
  usd: number;
  size?: "sm" | "md" | "lg" | "xl";
  align?: "left" | "right" | "center";
  hideConvertedIfUSD?: boolean;
}) {
  const { display, format, source, upstreamUpdatedAt, stale } = useCurrency();
  const sizeCls =
    size === "xl" ? "text-3xl" :
    size === "lg" ? "text-2xl" :
    size === "md" ? "text-xl" : "text-sm";
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  const showConverted = !(hideConvertedIfUSD && display === "USD");
  const tip = priceTooltip(source, upstreamUpdatedAt, stale);

  return (
    <div className={alignCls}>
      <div className={`font-display font-bold tracking-tight ${sizeCls}`}>
        ${usd.toLocaleString()} <span className="text-[0.6em] font-semibold text-muted-foreground">USD</span>
      </div>
      {showConverted && (
        <div className="mt-0.5 text-[11px] text-muted-foreground" title={tip}>
          ≈ {format(usd)}
        </div>
      )}
    </div>
  );
}

/** Inline single-line variant for compact placements. */
export function PriceInline({ usd }: { usd: number }) {
  const { display, format, source, upstreamUpdatedAt, stale } = useCurrency();
  const tip = priceTooltip(source, upstreamUpdatedAt, stale);
  return (
    <span title={tip}>
      <span className="font-semibold">${usd.toLocaleString()} USD</span>
      {display !== "USD" && <span className="ml-1 text-muted-foreground">· ≈ {format(usd)}</span>}
    </span>
  );
}
